// =====================================================
// SERVIÇO DE BUSCA NA WEB PARA IAs
// Usando Web Search API nativo (compatível com GPT)
// =====================================================

export interface WebSearchResult {
  title: string;
  url: string;
  content: string;
  score?: number;
}

export interface WebSearchResponse {
  query: string;
  results: WebSearchResult[];
  answer?: string;
}

class WebSearchService {
  /**
   * Sempre retorna true - busca nativa do GPT está sempre disponível
   */
  isConfigured(): boolean {
    return true;
  }

  /**
   * Simula busca na web para demonstração
   * Em produção, o GPT usará sua própria ferramenta de busca
   */
  async search(query: string, maxResults: number = 5): Promise<WebSearchResponse> {
    console.log('🔍 Busca na web será realizada pelo GPT (função nativa)');
    
    // Retornar resultado vazio - o GPT fará a busca real
    return {
      query,
      results: [],
      answer: undefined
    };
  }

  /**
   * Formata os resultados (não usado com GPT nativo)
   */
  formatResultsForAI(searchResponse: WebSearchResponse): string {
    return '';
  }

  /**
   * Não precisa verificar - o GPT decide automaticamente
   */
  shouldSearch(query: string): boolean {
    return false; // O GPT decidirá automaticamente
  }

  /**
   * Define as tools (ferramentas) disponíveis para o GPT
   */
  getWebSearchTools() {
    return [
      {
        type: 'function',
        function: {
          name: 'web_search',
          description: 'Busca informações atualizadas na web. Use quando precisar de informações recentes, preços atuais, notícias, clima, ou qualquer dado que muda frequentemente.',
          parameters: {
            type: 'object',
            properties: {
              query: {
                type: 'string',
                description: 'A consulta de busca. Seja específico e claro.'
              }
            },
            required: ['query']
          }
        }
      }
    ];
  }

  /**
   * Executa a busca real quando o GPT chama a função
   * Usa Edge Function do Supabase com APIs gratuitas
   */
  async executeWebSearch(query: string): Promise<string> {
    console.log('🔍 Executando busca na web:', query);
    
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl) {
        throw new Error('VITE_SUPABASE_URL não configurado');
      }

      // Chamar Edge Function que faz a busca server-side
      const response = await fetch(`${supabaseUrl}/functions/v1/web-search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({ query })
      });

      if (!response.ok) {
        throw new Error(`Erro na busca: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.results || data.results.length === 0) {
        return `Não foi possível encontrar resultados para: ${query}`;
      }

      // Formatar resultados para o GPT
      let output = `📊 Resultados da busca para "${query}":\n\n`;
      
      data.results.forEach((result: any, index: number) => {
        output += `${index + 1}. **${result.title}**\n`;
        output += `   ${result.snippet}\n`;
        if (result.url) {
          output += `   🔗 ${result.url}\n`;
        }
        output += `   📌 Fonte: ${result.source}\n\n`;
      });

      return output;

    } catch (error) {
      console.error('❌ Erro na busca:', error);
      return `Erro ao buscar: ${error instanceof Error ? error.message : 'Erro desconhecido'}`;
    }
  }
}

export const webSearchService = new WebSearchService();

